<template>

    <form>
        <div 
            v-for="(interval, index) in form.intervals" 
            :key="index + 1"
            class="form-row"
            :class="{ 'work-time': interval.work_interval_type == 'work', 'absence-time': interval.work_interval_type != 'work', 'nvp-time': interval.work_interval_type == 'nvp-holiday' }"
        >

            <div class="form-group col-sm-5">
                <div class="input-group">
                    <select class="custom-select" 
                            v-model="interval.work_interval_type"
                            v-validate="'required'" 
                    >
                        <option 
                            v-for="(workIntervalType) in workIntervalTypes" 
                            :value="workIntervalType.key"
                            :key="workIntervalType.key"
                            v-if="canShowAbsenceTypeItem(workIntervalType)"
                        >
                            {{ workIntervalType.label }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="form-group col-sm-3">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text"><i class="fal fa-fw fa-clock"></i></span>
                    </div>
                    <datetime 
                        v-model="interval.from" 
                        :config="timePickerConfig" 
                        v-validate="'date_format:mm:ss'"
                        class="flatpickr"
                    />
                </div>
            </div>

            <div class="form-group col-sm-3">
                <div class="input-group">
                    <datetime 
                        v-model="interval.to" 
                        :config="timePickerConfig" 
                        v-validate="'date_format:mm:ss'" 
                        class="flatpickr"
                    />
                    <div class="input-group-append">
                        <span class="input-group-text"><i class="fal fa-fw fa-clock"></i></span>
                    </div>
                </div>
            </div>

            <div class="form-group col-sm-11 d-flex align-items-baseline mb-3" v-if="interval.work_interval_type == 'nvp-holiday'">
                <label class="col-5 text-right px-0 ml-2"> 
                    <i class="fal fa-level-up-alt fa-fw" style="transform: rotate(90deg);"></i>
                    {{ trans.form.select_holiday }}:
                </label>

                <div class="input-group col-7 pl-0 pr-4 ml-3">
                    <div class="input-group-prepend">
                        <span class="input-group-text"><i class="fal fa-fw fa-list"></i></span>
                    </div>
                    <select class="custom-select" 
                            name='compensation_holiday'
                            v-model="interval.compensation_holiday"
                            v-validate="(interval.work_interval_type == 'nvp-holiday' ? 'required' : '')"
                            @change="holidaySelectChanged($event, interval, index)"
                    >
                        <option 
                            v-for="(item) in workedHolidays" 
                            :key="item.id"
                            :value="item.id"
                            v-if="item.compensation_type != 1 && ( !item.compensated_by || item.id == interval.compensation_holiday )"
                        >
                            {{ formatHolidaySelectOptionDate(item.date) }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="form-group col-sm-1">
                <button 
                    @click="removeInterval(index)"
                    type="button"
                    class="btn btn-danger btn-remove-interval"
                    :disabled="form.intervals.length == 1"
                >
                    <i class="fal fa-minus"></i>
                </button>
            </div>

        </div>

        <button
            @click="addInterval()"
            type="button"
            class="btn btn-info btn-repeater"
            :disabled="intervalsOverlap"
        >
            <i class="fal fa-plus"></i>
        </button>

        <span class="badge badge-interval badge-success float-right">
            {{ intervalLength() }} 
        </span>

    </form>

</template>


<script>
    import { intervalsOverlap, sortIntervalsByFrom } from '../../utils';

    const userLanguage = document.documentElement.lang;

    export default {
        name: 'work-day-embed-form',
        data: function() {
            return {
                url: null,
                date: null,
                form: {
                    intervals: []
                },
                timePickerConfig: {
                    enableTime: true,
                    noCalendar: true,
                    time_24hr: true,
                    enableSeconds: false,
                    dateFormat: 'H:i',
                    defaultHour: 8,
                    defaultMinute: 0, 
                    locale: userLanguage
              }
            }
        },

        props: {
            'workIntervalTypes': {
                type: Array
            },
            'workedHolidays': {
                type: Array
            },
            'defaultFormData': {
                type: Object
            },
            'contractSettings': {
                type: Object
            },
            'trans': {
                type: Object
            },
            'defaultInterval': {
                type: Object
            },
            'dailyWorkingFund': {
                type: Number,
                required: false,
                default: () => {
                    return 7;
                }
            }
        },

        computed: {
            intervalsOverlap() {
                return intervalsOverlap(this.form.intervals);
            }
        },

        watch: {
            intervalsOverlap: {
                immediate: true,
                handler(value) {
                    this.$emit('intervals-overlap', value);
                }
            }
        },

        created() {
            this.form = this.defaultFormData;

            this.sortIntervals();
        },

        methods: {
            addInterval() {
                const lastInterval = this.form.intervals[this.form.intervals.length - 1];
                
                if (!lastInterval) {
                    this.form.intervals.push({
                        ...this.defaultInterval
                    })
                    return;
                }

                let mmtLastIntervalFrom = moment(lastInterval.from,'HH:mm');
                let mmtLastIntervalTo = moment(lastInterval.to,'HH:mm');

                if(mmtLastIntervalTo.unix() < mmtLastIntervalFrom.unix()) {
                    mmtLastIntervalTo = mmtLastIntervalTo.add(1, 'day');
                }
                
                let lastIntervalRange = moment.range(mmtLastIntervalFrom, mmtLastIntervalTo);
                let lastIntervalLength = Math.abs( lastIntervalRange.diff('hours') ) + ( Math.abs(lastIntervalRange.diff('minutes')) - Math.abs(lastIntervalRange.diff('hours')*60) ) / 60;
                
                let remainingShiftLength = this.dailyWorkingFund - lastIntervalLength;
                if(remainingShiftLength < 0) remainingShiftLength = 1;
                
                let breakLength = 0.5; // h
                let nextFrom = moment(lastInterval.to, 'HH:mm').add(breakLength, 'h').format('HH:mm');
                let nextEnd = moment(nextFrom, 'HH:mm').add(remainingShiftLength, 'h').format('HH:mm');
                
                this.form.intervals.push({
                    ...this.defaultInterval,
                    work_interval_type: 'work',
                    from: nextFrom,
                    to: nextEnd
                });
            },

            removeInterval(index) {
                this.$delete(this.form.intervals, index);
            },

            intervalLength() {
                let totalSec = 0;

                this.form.intervals.forEach( interval => {
                    let t1 = moment(interval.from, "hh:mm").diff(moment().startOf('day'), 'seconds');
                    let t2 = moment(interval.to, "hh:mm").diff(moment().startOf('day'), 'seconds');

                    totalSec += (t2 - t1);
                });

                return moment().startOf('day').seconds(totalSec).format('HH:mm') + ' ' + this.trans.hours;
            },

            validateIntervals() {
                return this.intervalsOverlap;
            },

            validateWorkedHolidays() {
                let numberOfCompensatedHolidayHours = this.getHolidayHoursFromIntervals(this.form.intervals);
                let numberOfWorkedHolidayHours = this.getWorkedHolidayHoursFromIntervals(this.form.intervals);
                
                if(numberOfCompensatedHolidayHours > 0)
                {
                    if(numberOfWorkedHolidayHours < numberOfCompensatedHolidayHours) {
                        return 1;
                    }
                    else if(numberOfWorkedHolidayHours > numberOfCompensatedHolidayHours) {
                        return -1;
                    }
                }
                
                return 0;
            },
            
            getHolidayHoursFromIntervals(intervals)
            {
                let hours = 0;
                
                for(let x in intervals)
                {
                    if(intervals[x].work_interval_type == "nvp-holiday")
                    {
                        let t1 = moment(intervals[x].from, "hh:mm").diff(moment().startOf('day'), 'minutes');
                        let t2 = moment(intervals[x].to, "hh:mm").diff(moment().startOf('day'), 'minutes');
                        
                        hours += (t2 - t1) / 60;
                    }
                }
                
                return hours;
            },
            
            getWorkedHolidayHoursFromIntervals(intervals)
            {
                for(var x in intervals)
                {
                    if(intervals[x] && intervals[x].work_interval_type == "nvp-holiday")
                    {
                        var compHolidayId = intervals[x].compensation_holiday;
                        let foundWH = this.workedHolidays.find(x => x.id == compHolidayId);
                        return (foundWH ? foundWH.length : 0);
                    }
                }
                
                return 0;
            },

            getData() {
                return this.form;
            },

            formatHolidaySelectOptionDate(val) {
                return moment(val).format('DD.MM.YYYY');
            },

            canShowAbsenceTypeItem(workDayType) {
                return workDayType.key != 'nvp-holiday' && workDayType.key != 'holiday-not-worked' || 
                       workDayType.key == 'nvp-holiday' && this.contractSettings.workedHolidayCompensationClaim == 1 ||
                       workDayType.key == 'holiday-not-worked' && this.contractSettings.calculateWorkFundWithHoliday == 1;
            },

            holidaySelectChanged(evt, interval, index) {
                const foundWH = this.workedHolidays.find(x => x.id == evt.srcElement.value);
    
                if(foundWH) {
                    let workedHolidayHours = foundWH.length;

                    if(index == 0) {
                        interval.from = moment().hours(7).minutes(0).seconds(0).format('HH:mm');
                    }
                    else {
                        interval.from = moment(this.getSortedIntervals[index-1].to, 'HH:mm').format('HH:mm');
                    }

                    interval.to = moment(interval.from, 'HH:mm').add(workedHolidayHours, 'h').format('HH:mm');
                }
            },

            getNvpHolidayState() {
                let validationState = this.validateWorkedHolidays();  // 1 (error) / -1 (warning) / 0 (is ok)
                let availableHours = this.getWorkedHolidayHoursFromIntervals(this.form.intervals);

                return {
                    state: validationState === 0,
                    variant: validationState === 1 ? 'danger' : 'warning',
                    hours: Math.round(availableHours * 100) / 100
                };
            },

            sortIntervals() {
                this.form.intervals = sortIntervalsByFrom(this.form.intervals);
            }

        }

    }
</script>


// WEBPACK FOOTER //
// resources/assets/admin/js/attendance/modals/WorkDayEmbedForm.vue